<script setup lang="ts">
import { computed, PropType } from 'vue'
import { HelpTextTooltip } from '@haskoning/vue-component-library'
import { InputType } from '@/stores/types/types'
import GenericField from '@/components/form/GenericField.vue'
import GroupRowField from '@/components/form/GroupRowField.vue'
import type { Field } from '@haskoning/vue-component-library'

type RuleFunction = (value: number, key: string) => true | string

const props = defineProps({
	field: {
		type: Object as PropType<Field>,
		required: true
	},
	modelValue: {
		type: Object as PropType<Record<string, any>>,
		required: true
	},
	equalColumns: {
		type: Boolean,
		default: false
	},
	isReadOnly: {
		type: Boolean,
		default: false
	},
	customErrorRules: {
		type: Function as PropType<RuleFunction>,
		default: () => true
	},
	helpText: {
		type: String,
		default: ''
	},
	hasHelpText: {
		type: Function as PropType<(field: string) => boolean>,
		required: false,
		default: () => false
	},
	disabled: {
		type: Boolean,
		default: false
	},
	type: {
		type: [String, null] as PropType<InputType | null>,
		default: null
	},
	inline: {
		type: Boolean,
		default: true
	}
})

const emit = defineEmits<{
	(e: 'update:modelValue', value: Record<string, any>): void
	(e: 'update'): void
}>()

const vModel = computed({
	get: () => props.modelValue,
	set: (value: Record<string, any>) => {
		emit('update:modelValue', value)
	}
})

const inputType = computed<InputType>(() => props.type ?? (props.field.type as InputType) ?? 'text')

const checkHelpText = (field: string): boolean => {
	return props.hasHelpText(field) || props.helpText !== ''
}
</script>

<template>
	<GroupRowField :has-help-text="checkHelpText(field.parameter)" :equal-columns="equalColumns">
		<template v-slot:append-label>
			<HelpTextTooltip
				v-if="checkHelpText(field.parameter)"
				:text="field.help_text || helpText"
				max-width="800px"
			/>
		</template>
		<template v-slot:label>
			{{ field.label }}
		</template>

		<template v-slot:field>
			<GenericField
				v-model="vModel"
				:field="field"
				:type="inputType"
				:readonly="isReadOnly"
				:disabled="disabled"
				v-bind="$attrs"
				@update="emit('update')"
			/>
		</template>
	</GroupRowField>
</template>
